import { useTranslation } from '@frontend/i18n';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { Modal, ModalControlModalProps, useAlert, useModalControl } from '@frontend/design-system';
import { trackingId } from '../../tracking';
import { logError } from '../../utils/loggers';
import { DeleteFlow } from '../common/delete-flow';
import { usePhoneTreeMutations } from './use-phone-tree-mutations';

export const DeletePhoneTreeButton = ({
  name,
  id,
  phoneTenantId,
  disabled,
}: {
  name: string;
  id: string;
  phoneTenantId: string;
  disabled?: boolean;
}) => {
  const { t } = useTranslation('phone');
  const { modalProps, triggerProps } = useModalControl();

  return (
    <DeleteFlow.Button
      label={t('Delete Phone Tree')}
      trackingId={trackingId({ context: 'setting', feature: 'phone-trees-quick-actions', details: 'delete' })}
      triggerProps={triggerProps}
      disabled={disabled}
      disabledTooltip={t('You can only delete this Phone Tree from its associated Call Route.')}
    >
      <PhoneTreeDeleteModal modalProps={modalProps} name={name} id={id} phoneTenantId={phoneTenantId} />
    </DeleteFlow.Button>
  );
};

export const DeletePhoneTreeIconButton = ({
  name,
  id,
  phoneTenantId,
  disabled,
}: {
  name: string;
  id: string;
  phoneTenantId: string;
  disabled?: boolean;
}) => {
  const { t } = useTranslation('phone');
  const { modalProps, triggerProps } = useModalControl();

  return (
    <DeleteFlow.IconButton
      label={
        disabled ? t('You can only delete this Phone Tree from its associated Call Route.') : t('Delete Phone Tree')
      }
      triggerProps={triggerProps}
      disabled={disabled}
      trackingId={trackingId({ context: 'setting', feature: 'phone-trees-quick-actions', details: 'index-delete' })}
    >
      <PhoneTreeDeleteModal modalProps={modalProps} name={name} id={id} phoneTenantId={phoneTenantId} />
    </DeleteFlow.IconButton>
  );
};

const PhoneTreeDeleteModal = ({
  modalProps,
  name,
  id,
  phoneTenantId,
}: {
  modalProps: ModalControlModalProps;
  name: string;
  id: string;
  phoneTenantId: string;
}) => {
  return (
    <Modal {...modalProps} minWidth={600}>
      <PhoneTreeCanDeleteContent modalProps={modalProps} name={name} id={id} phoneTenantId={phoneTenantId} />
    </Modal>
  );
};

const PhoneTreeCanDeleteContent = ({
  modalProps,
  name,
  id,
  phoneTenantId,
}: {
  modalProps: ModalControlModalProps;
  name: string;
  id: string;
  phoneTenantId: string;
}) => {
  const { remove } = usePhoneTreeMutations(phoneTenantId);
  const { t } = useTranslation('phone');
  const alerts = useAlert();
  const { navigate } = useSettingsNavigate();

  const removeFn = () =>
    remove.mutate(
      { phoneTreeId: id },
      {
        onSuccess: () => {
          alerts.success(t('Phone Tree deleted successfully.'));
          modalProps.onClose();
          navigate({ to: '/phone/phone-tree-quick-actions' });
        },
        onError: () => {
          alerts.error(t('System failed to delete Phone Tree. Please try again.'));
          logError('PhoneTreeCanDeleteContent:removeMutation | Failed to delete phone tree', {
            data: { phoneTreeId: id, phoneTenantId },
          });
        },
      }
    );

  return (
    <DeleteFlow.CanDeleteContent
      modalProps={modalProps}
      name={name}
      title={t('Delete Phone Tree?')}
      removeFn={removeFn}
    />
  );
};
